<template>
  <div class="redeem-gift-page">
    <div class="container">
      <div class="gift-icon-wrapper">
        <i class="fa-solid fa-gift gift-icon"></i>
      </div>
      <h1 class="title">{{ validationPurchaserName ? `Accetta il regalo da ${validationPurchaserName}` : 'Accetta il regalo' }}</h1>
      <p class="subtitle">Inserisci il codice per riscattare il tuo regalo. I servizi verranno applicati al tuo profilo.</p>

      <div class="card">
        <div v-if="successMessage" class="alert alert-danger">
          <i class="fa-solid fa-circle-check"></i>
          <div class="alert-content">
            <div class="fw-bold">Il regalo è stato aggiunto al tuo account</div>
            <div v-if="walletBalance !== null" class="mt-2">
              Nuovo saldo Wallet: <strong>{{ walletBalance.toFixed(2) }}€</strong>
            </div>
            <div v-if="successMessage && !validationAmount" class="mt-3">
              <button @click="openDeepLink" class="btn btn-custom">
                <i class="fa-solid fa-mobile-screen-button me-2"></i>Vai all'app
              </button>
            </div>
          </div>
        </div>

        <div v-if="errorMessage" class="alert alert-danger">
          <i class="fa-solid fa-triangle-exclamation"></i>
          <div>{{ errorMessage }}</div>
        </div>

        <div v-if="!successMessage">
          <div class="form-group">
            <label class="form-label">Codice Gift</label>
            <input
              type="text"
              class="form-control code-input"
              v-model="code"
              :disabled="isValidating || isRedeeming"
              placeholder="GIFT-XXXX-XXXX"
              @input="onCodeInput"
            />
            <div v-if="isValidating" class="mt-2 text-center">
              <i class="fa-solid fa-spinner fa-spin me-2"></i>
              <span>Verifica in corso...</span>
            </div>
          </div>

          <div v-if="validationValid && validationServices && validationServices.length > 0" class="services-preview">
            <h5 class="fw-bold mb-3">Servizi inclusi nel regalo:</h5>
            <ul class="services-list">
              <li v-for="(service, index) in validationServices" :key="index">
                <strong>{{ service.name }}</strong>
                <span v-if="service.variation"> - {{ service.variation }}</span>
              </li>
            </ul>
          </div>

          <div v-if="validationValid && validationAmount" class="services-preview">
            <h5 class="fw-bold mb-3">Importo Gift Card:</h5>
            <div class="amount-display">{{ validationAmount.toFixed(2) }}€</div>
          </div>

          <button
            class="btn w-100 mt-3"
            :class="validationValid ? 'btn-custom' : 'btn-disabled'"
            :disabled="!validationValid || isRedeeming"
            @click="onRedeem"
          >
            <span v-if="isRedeeming"><i class="fa-solid fa-spinner fa-spin me-1"></i>Riscatto in corso...</span>
            <span v-else>Riscatta</span>
          </button>

          <div v-if="validationValid || successMessage || errorMessage" class="text-center mt-3">
            <button class="btn btn-link" @click="onReset">Riscatta un altro codice</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { API_CONFIG } from '@/config/api';

export default {
  name: 'RedeemGiftCard',
  data() {
    return {
      code: '',
      validationValid: false,
      validationAmount: null,
      validationRecipient: null,
      validationServices: [],
      validationPurchaserName: null,
      successMessage: '',
      errorMessage: '',
      walletBalance: null,
      isValidating: false,
      isRedeeming: false,
      validateTimeout: null,
    };
  },
  mounted() {
    // Preleva il codice dall'URL se presente
    const codeFromUrl = this.$route.query?.code;
    if (codeFromUrl) {
      this.code = codeFromUrl;
    }

    // se non autenticato, rimanda al login/registrazione mantenendo il codice nell'URL
    const token = localStorage.getItem('userToken');
    if (!token) {
      const returnUrl = codeFromUrl 
        ? `/redeem-gift-card?code=${encodeURIComponent(codeFromUrl)}`
        : '/redeem-gift-card';
      this.$router.replace({ path: '/login', query: { returnUrl } });
      return; // Importante: esci subito per evitare esecuzione di codice successivo
    }
    
    // Verifica se il token è scaduto
    const tokenExpiration = localStorage.getItem('tokenExpiration');
    if (tokenExpiration) {
      const expirationTime = parseInt(tokenExpiration, 10);
      if (expirationTime && Date.now() > expirationTime) {
        // Token scaduto: rimuovi e reindirizza al login
        localStorage.removeItem('userToken');
        localStorage.removeItem('tokenExpiration');
        const returnUrl = codeFromUrl 
          ? `/redeem-gift-card?code=${encodeURIComponent(codeFromUrl)}`
          : '/redeem-gift-card';
        this.$router.replace({ path: '/login', query: { returnUrl } });
        return;
      }
    }
    
    // Se autenticato e c'è un codice nell'URL, valida automaticamente
    if (codeFromUrl) {
      this.onValidate();
    } else if (this.code) {
      // Se c'è un codice nel campo, valida automaticamente
      this.onValidate();
    }
  },
  methods: {
    apiHeaders() {
      const token = localStorage.getItem('userToken');
      return {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      };
    },
    async onValidate() {
      if (!this.code) {
        this.errorMessage = 'Inserisci un codice gift card';
        return;
      }
      this.isValidating = true;
      this.errorMessage = '';
      this.successMessage = '';
      this.validationValid = false;
      try {
        const res = await fetch(
          `${API_CONFIG.BASE_URL}/api/gift-cards/validate/${encodeURIComponent(this.code)}`,
          { headers: this.apiHeaders() }
        );
        
        // Se la risposta è 401 (Unauthorized), significa che il token è invalido/scaduto
        if (res.status === 401) {
          // Rimuovi il token invalido
          localStorage.removeItem('userToken');
          localStorage.removeItem('tokenExpiration');
          
          // Reindirizza al login mantenendo il codice nell'URL
          const codeFromUrl = this.$route.query?.code || this.code;
          const returnUrl = codeFromUrl 
            ? `/redeem-gift-card?code=${encodeURIComponent(codeFromUrl)}`
            : '/redeem-gift-card';
          this.$router.replace({ path: '/login', query: { returnUrl } });
          return;
        }
        
        const data = await res.json();
        if (!res.ok || data.valid === false) {
          this.errorMessage = data?.message || 'Codice non valido';
          return;
        }
        this.validationValid = true;
        // Gestisce sia gift card prodotto (con amount) che checkout gift (senza amount)
        if (data.amount) {
          this.validationAmount = data.amount;
          this.validationServices = [];
        } else {
          this.validationAmount = null;
          // Per checkout gift, mostra i servizi inclusi
          this.validationServices = data.services || [];
        }
        this.validationRecipient = data.recipientEmail;
        this.validationPurchaserName = data.purchaserName || null;
      } catch (e) {
        this.errorMessage = e?.message || 'Errore durante la validazione';
      } finally {
        this.isValidating = false;
      }
    },
    async onRedeem() {
      if (!this.validationValid) {
        this.errorMessage = 'Verifica prima il codice.';
        return;
      }
      this.isRedeeming = true;
      this.errorMessage = '';
      this.successMessage = '';
      try {
        const res = await fetch(
          `${API_CONFIG.BASE_URL}/api/gift-cards/redeem/${encodeURIComponent(this.code)}`,
          {
            method: 'POST',
            headers: this.apiHeaders(),
            body: JSON.stringify({}),
          }
        );
        
        // Se la risposta è 401 (Unauthorized), significa che il token è invalido/scaduto
        if (res.status === 401) {
          // Rimuovi il token invalido
          localStorage.removeItem('userToken');
          localStorage.removeItem('tokenExpiration');
          
          // Reindirizza al login mantenendo il codice nell'URL
          const codeFromUrl = this.$route.query?.code || this.code;
          const returnUrl = codeFromUrl 
            ? `/redeem-gift-card?code=${encodeURIComponent(codeFromUrl)}`
            : '/redeem-gift-card';
          this.$router.replace({ path: '/login', query: { returnUrl } });
          return;
        }
        
        const data = await res.json();
        if (!res.ok || data.status === 'error') {
          this.errorMessage = data?.message || 'Errore durante il riscatto';
          return;
        }
        this.successMessage = data.message || (data.type === 'checkout_gift' 
          ? 'Gift riscattato con successo! I servizi sono stati applicati al tuo profilo.' 
          : 'Gift card riscattata con successo');
        this.walletBalance = data.walletBalance ?? null;
        this.validationValid = false;
        this.validationAmount = null;
        this.validationRecipient = null;
        this.validationServices = [];
        this.code = '';
      } catch (e) {
        this.errorMessage = e?.message || 'Errore durante il riscatto';
      } finally {
        this.isRedeeming = false;
      }
    },
    onCodeInput() {
      // Validazione automatica quando l'utente digita (con debounce)
      if (this.code && this.code.length >= 4) {
        clearTimeout(this.validateTimeout);
        this.validateTimeout = setTimeout(() => {
          this.onValidate();
        }, 500);
      } else {
        // Reset se il codice è troppo corto
        this.validationValid = false;
        this.validationAmount = null;
        this.validationRecipient = null;
        this.validationServices = [];
        this.validationPurchaserName = null;
        this.errorMessage = '';
      }
    },
    onReset() {
      this.code = '';
      this.validationValid = false;
      this.validationAmount = null;
      this.validationRecipient = null;
      this.validationServices = [];
      this.validationPurchaserName = null;
      this.successMessage = '';
      this.errorMessage = '';
      this.walletBalance = null;
      if (this.validateTimeout) {
        clearTimeout(this.validateTimeout);
      }
    },
    openDeepLink() {
      const deeplink = "elitefitness://elitefitness.it";
      const iosStore = "https://apps.apple.com/it/app/id6670614281";
      const androidStore = "https://play.google.com/store/apps/details?id=it.utopialabs.elitefitness";
      const fallbackWeb = "https://elitefitness.it";

      const ua = navigator.userAgent || navigator.vendor || window.opera;
      let storeUrl;

      if (/android/i.test(ua)) {
        storeUrl = androidStore;
      } else if (/iPad|iPhone|iPod/.test(ua) && !window.MSStream) {
        storeUrl = iosStore;
      } else {
        // Desktop o altro → vai al sito
        window.location = fallbackWeb;
        return;
      }

      const now = Date.now();
      window.location = deeplink;

      setTimeout(() => {
        if (Date.now() - now < 1600) {
          window.location = storeUrl;
        }
      }, 1500);
    },
  },
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@400;600;700&display=swap');

.redeem-gift-page {
  font-family: 'Montserrat', sans-serif;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  background-color: #292D7D;
  color: white;
  padding: 2rem;
}

.container {
  max-width: 600px;
  width: 100%;
}

.gift-icon-wrapper {
  margin-bottom: 1.5rem;
  display: flex;
  justify-content: center;
  align-items: center;
}

.gift-icon {
  font-size: 4rem;
  color: #e83539;
  animation: bounce 2s infinite;
}

@keyframes bounce {
  0%, 20%, 50%, 80%, 100% {
    transform: translateY(0);
  }
  40% {
    transform: translateY(-10px);
  }
  60% {
    transform: translateY(-5px);
  }
}

.title {
  font-family: 'Montserrat', sans-serif;
  font-size: 2rem;
  font-weight: 700;
  color: white;
  margin-bottom: 1rem;
}

.subtitle {
  font-family: 'Montserrat', sans-serif;
  font-size: 1rem;
  color: rgba(255, 255, 255, 0.85);
  margin-bottom: 2rem;
}

.card {
  background-color: rgba(255, 255, 255, 0.1);
  border: 2px solid rgba(255, 255, 255, 0.2);
  border-radius: 0;
  padding: 2rem;
  margin-bottom: 2rem;
}

.form-group {
  margin-bottom: 1.5rem;
  text-align: left;
}

.form-label {
  font-family: 'Montserrat', sans-serif;
  font-weight: 600;
  color: white;
  display: block;
  margin-bottom: 0.5rem;
}

.form-control {
  font-family: 'Montserrat', sans-serif;
  background-color: rgba(255, 255, 255, 0);
  border: 2px solid white;
  border-radius: 0;
  color: white;
  padding: 10px 15px;
}

.form-control::placeholder {
  color: rgba(255, 255, 255, 0.6);
}

.form-control:focus {
  background-color: rgba(255, 255, 255, 0.05);
  border-color: white;
  color: white;
  outline: none;
  box-shadow: none;
}

.code-input {
  font-family: 'Montserrat', monospace;
  text-transform: uppercase;
  letter-spacing: 1px;
}

.input-group {
  display: flex;
  gap: 0.5rem;
}

.input-group .form-control {
  flex: 1;
}

.btn {
  font-family: 'Montserrat', sans-serif;
  border-radius: 0;
  text-transform: uppercase;
  font-weight: 600;
  padding: 10px 20px;
  border: none;
  cursor: pointer;
  transition: background-color 0.3s ease;
}

.btn-custom {
  background-color: #e83539;
  color: white;
  font-size: 1.2rem;
}

.btn-custom:hover {
  background-color: #cc2e31;
  color: white;
}

.btn-custom:disabled {
  background-color: #999;
  cursor: not-allowed;
}

.btn-disabled {
  background-color: #666;
  color: rgba(255, 255, 255, 0.5);
  cursor: not-allowed;
}

.btn-outline {
  background-color: transparent;
  border: 2px solid white;
  color: white;
  font-size: 1rem;
}

.btn-outline:hover:not(:disabled) {
  background-color: rgba(255, 255, 255, 0.1);
  color: white;
}

.btn-outline:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-link {
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.8);
  text-decoration: underline;
  font-size: 0.9rem;
  cursor: pointer;
  padding: 0;
}

.btn-link:hover {
  color: white;
}

.alert {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  padding: 1rem;
  margin-bottom: 1.5rem;
  border-radius: 0;
  border: 2px solid;
}

.alert-success {
  background-color: rgba(40, 167, 69, 0.2);
  border-color: #28a745;
  color: white;
}

.alert-danger {
  background-color: rgba(232, 53, 57, 0.2);
  border-color: #e83539;
  color: white;
}

.alert-info {
  background-color: rgba(23, 162, 184, 0.2);
  border-color: #17a2b8;
  color: white;
}

.alert-content {
  flex: 1;
  text-align: left;
}

.alert i {
  font-size: 1.5rem;
  margin-top: 0.2rem;
}

.services-preview {
  background-color: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 0;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  text-align: left;
}

.services-preview h5 {
  font-family: 'Montserrat', sans-serif;
  font-weight: 700;
  color: white;
  margin-bottom: 1rem;
}

.services-list {
  list-style: none;
  padding-left: 0;
  margin: 0;
  text-align: left;
}

.services-list li {
  margin-bottom: 0.75rem;
  padding-left: 1.5rem;
  position: relative;
  font-size: 1rem;
  line-height: 1.5;
  color: white;
}

.services-list li::before {
  content: "•";
  position: absolute;
  left: 0;
  color: white;
  font-weight: bold;
  font-size: 1.2rem;
}

.amount-display {
  font-size: 1.5rem;
  font-weight: 700;
  color: white;
  text-align: center;
  padding: 1rem;
  background-color: rgba(255, 255, 255, 0.1);
  border: 2px solid rgba(255, 255, 255, 0.3);
}

.form-text {
  display: block;
  margin-top: 0.5rem;
  font-size: 0.875rem;
  color: rgba(255, 255, 255, 0.7);
}

.info-box {
  background-color: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 0;
  color: white;
  padding: 1.5rem;
  text-align: left;
}

.info-box h5 {
  font-family: 'Montserrat', sans-serif;
  font-weight: 700;
  color: white;
  margin-bottom: 1rem;
}

.info-box ul {
  list-style: none;
  padding-left: 0;
  margin: 0;
}

.info-box li {
  margin-bottom: 0.5rem;
  padding-left: 1.5rem;
  position: relative;
}

.info-box li::before {
  content: "•";
  position: absolute;
  left: 0;
  color: white;
  font-weight: bold;
}

.w-100 {
  width: 100%;
}.mt-2 {
  margin-top: 0.5rem;
}.mt-3 {
  margin-top: 1rem;
}.mb-2 {
  margin-bottom: 0.5rem;
}.me-1 {
  margin-right: 0.25rem;
}.me-2 {
  margin-right: 0.5rem;
}.fw-bold {
  font-weight: 700;
}.text-center {
  text-align: center;
}.text-left {
  text-align: left;
}
</style>
